import { useState, useMemo } from 'react';

export default function useContactList(rows = []) {
  // Sorting State
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  // Filtering State: { colId: Set(['Value 1', 'Value 2']) }
  const [columnFilters, setColumnFilters] = useState({});
  const [filterMenu, setFilterMenu] = useState({ anchorEl: null, columnKey: null });

  // Pagination State
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  const [stageMenu, setStageMenu] = useState({ anchorEl: null, row: null });
  const [actionMenu, setActionMenu] = useState({ anchorEl: null, row: null });

  // 1. Get Unique Values for the currently opened filter menu
  const activeColUniqueValues = useMemo(() => {
    if (!filterMenu.columnKey) return [];
    const valuesSet = new Set();
    rows.forEach((row) => {
      const val = row[filterMenu.columnKey];
      valuesSet.add(val != null && val !== '' ? String(val) : '(Blank)');
    });
    return Array.from(valuesSet).sort((a, b) =>
      a.localeCompare(b, undefined, { numeric: true })
    );
  }, [rows, filterMenu.columnKey]);

  // 2. Handle Sort Toggle (A-Z -> Z-A -> Clear)
  const handleSortClick = (colId) => {
    setPage(0);
    setSortConfig((prev) => {
      if (prev.key !== colId) return { key: colId, direction: 'asc' };
      if (prev.direction === 'asc') return { key: colId, direction: 'desc' };
      return { key: null, direction: 'asc' }; // 3rd click clears
    });
  };

  // 3. Open & Close Filter Menu
  const openFilterMenu = (event, colId) => {
    event.stopPropagation();
    setFilterMenu({ anchorEl: event.currentTarget, columnKey: colId });
  };

  const closeFilterMenu = () => {
    setFilterMenu({ anchorEl: null, columnKey: null });
  };

  // 4. Instant Live Filters
  const toggleFilterValue = (value) => {
    setPage(0);
    setColumnFilters((prev) => {
      const key = filterMenu.columnKey;
      const current = new Set(prev[key] ?? []);

      if (current.has(value)) current.delete(value);
      else current.add(value);

      return { ...prev, [key]: new Set(current) };
    });
  };

  const selectAllColumn = () => {
    setPage(0);
    setColumnFilters((prev) => ({
      ...prev,
      [filterMenu.columnKey]: new Set(activeColUniqueValues),
    }));
  };

  const clearColumnFilter = (keyToClear = filterMenu.columnKey) => {
    setPage(0);
    setColumnFilters((prev) => {
      const updated = { ...prev };
      delete updated[keyToClear];
      return updated;
    });
  };

  // 5. Apply Filters
  const filteredRows = useMemo(() => {
    let result = rows;

    Object.entries(columnFilters).forEach(([colId, selectedSet]) => {
      if (selectedSet && selectedSet.size > 0) {
        result = result.filter((row) => {
          const cellValue = row[colId] != null && row[colId] !== '' ? String(row[colId]) : '(Blank)';
          return selectedSet.has(cellValue);
        });
      }
    });

    return result;
  }, [rows, columnFilters]);

  // 6. Apply Sorting
  const sortedRows = useMemo(() => {
    if (!sortConfig.key) return filteredRows;

    return [...filteredRows].sort((a, b) => {
      const valA = (a[sortConfig.key] ?? '').toString().toLowerCase();
      const valB = (b[sortConfig.key] ?? '').toString().toLowerCase();

      if (valA < valB) return sortConfig.direction === 'asc' ? -1 : 1;
      if (valA > valB) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredRows, sortConfig]);

  // 7. Paginate Results
  const paginatedRows = useMemo(() => {
    return sortedRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [sortedRows, page, rowsPerPage]);

  return {
    paginatedRows,
    totalCount: sortedRows.length,
    activeColUniqueValues,

    sortConfig,
    handleSortClick,

    columnFilters,
    filterMenu,
    openFilterMenu,
    closeFilterMenu,
    toggleFilterValue,
    selectAllColumn,
    clearColumnFilter,

    page,
    rowsPerPage,
    setPage,
    setRowsPerPage,

    stageMenu,
    setStageMenu,
    actionMenu,
    setActionMenu,
  };
}